import { useCallback, useEffect, useRef, useState } from 'react'
import {
  shouldValidateEventLeadPhone,
  validateEventLeadQuickContact,
  validateEventLeadQuickEmpresa,
} from '@/utils/eventLeadQuickForm'

type Args = {
  contato: string
  telefone: string
  empresa: string
  empresaId?: string
  skipEmpresaLookup?: boolean
}

export function useEventLeadQuickValidation({
  contato,
  telefone,
  empresa,
  empresaId,
  skipEmpresaLookup = false,
}: Args) {
  const [errors, setErrors] = useState<Record<string, string>>({})
  const phoneTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const empresaTimerRef = useRef<ReturnType<typeof setTimeout>>()

  const clearFieldError = useCallback((field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }, [])

  const setFieldErrors = useCallback((patch: Record<string, string>) => {
    setErrors((prev) => {
      const next = { ...prev }
      for (const [key, value] of Object.entries(patch)) {
        if (value) next[key] = value
        else delete next[key]
      }
      return next
    })
  }, [])

  const resetErrors = useCallback(() => setErrors({}), [])

  useEffect(() => {
    if (phoneTimerRef.current) clearTimeout(phoneTimerRef.current)

    if (!shouldValidateEventLeadPhone(telefone)) {
      clearFieldError('telefone')
      return
    }

    phoneTimerRef.current = setTimeout(async () => {
      const contactErrors = await validateEventLeadQuickContact(telefone, empresaId)
      setFieldErrors({ telefone: contactErrors.telefone ?? '' })
    }, 450)

    return () => {
      if (phoneTimerRef.current) clearTimeout(phoneTimerRef.current)
    }
  }, [telefone, empresaId, clearFieldError, setFieldErrors])

  useEffect(() => {
    if (empresaTimerRef.current) clearTimeout(empresaTimerRef.current)

    if (skipEmpresaLookup || !empresa.trim() || empresaId) {
      if (empresaId || skipEmpresaLookup) clearFieldError('empresa')
      return
    }

    empresaTimerRef.current = setTimeout(async () => {
      const result = await validateEventLeadQuickEmpresa(empresa)
      setFieldErrors({ empresa: result.errors.empresa ?? '' })
    }, 500)

    return () => {
      if (empresaTimerRef.current) clearTimeout(empresaTimerRef.current)
    }
  }, [empresa, empresaId, skipEmpresaLookup, clearFieldError, setFieldErrors])

  const validateEmpresaSelection = useCallback(async (selectedEmpresaId: string) => {
    const result = await validateEventLeadQuickEmpresa(empresa, selectedEmpresaId)
    setFieldErrors({ empresa: result.errors.empresa ?? '' })
    return result
  }, [empresa, setFieldErrors])

  const validateBeforeSubmit = useCallback(async () => {
    const nextErrors: Record<string, string> = {}

    if (!contato.trim()) nextErrors.contato = 'Contato é obrigatório'

    const contactErrors = await validateEventLeadQuickContact(telefone, empresaId)
    Object.assign(nextErrors, contactErrors)

    const empresaResult = await validateEventLeadQuickEmpresa(empresa, empresaId, {
      skipDuplicateLookup: skipEmpresaLookup,
    })
    Object.assign(nextErrors, empresaResult.errors)

    setErrors(nextErrors)
    return {
      errors: nextErrors,
      empresaId: empresaResult.empresaId ?? empresaId,
    }
  }, [contato, telefone, empresa, empresaId, skipEmpresaLookup])

  return {
    errors,
    setFieldErrors,
    clearFieldError,
    resetErrors,
    validateEmpresaSelection,
    validateBeforeSubmit,
  }
}
